import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Button, Flex } from '@zillow/constellation';
import { Container, Tag } from '@zillow/zrs-comet';

import { AppSelectors, UserSelectors, PropertiesSelectors } from 'app/shared/selectors';
import { STATUS_KEY, getTagTypeByKey } from 'app/shared/utils/statusTagUtils';
import userUtils from 'app/shared/utils/userUtils';
import zGuaranteeIcon from 'app/shared/images/z-guarantee.svg';
import AdditionalInformation from './components/AdditionalInformation';
import ErrorMessage from './components/ErrorMessage';
import LearnMore from './components/LearnMore';

import {
    StickyListingFooterContainer,
    StyledFlexContainer,
    StyledGuaranteeIcon,
    StyledMiddleFlex,
    StyledTagSpacer,
} from './StyledComponents';

const { makeIsScreenConstellationMobile, makeIsScreenConstellationTablet } = AppSelectors;
const { isPostingPathAdmin } = userUtils;

const propTypes = {
    formData: PropTypes.object.isRequired,
    onDeactivateClick: PropTypes.func.isRequired,
    onPublishClick: PropTypes.func.isRequired,
    hasRestrictionErrorMessage: PropTypes.bool.isRequired,
    paidListingExists: PropTypes.bool,
};

const defaultProps = {
    paidListingExists: false,
};

const ConnectedListingFooter = ({
    formData: {
        active,
        isActivationBlocked,
        isEditable,
        isPaidMultifamily,
        paidInclusion: {
            isEnabled: isInPaidMarket,
            isPaymentRequired,
            isPaymentValid,
            price,
        } = {},
        syndication,
    },
    hasRestrictionErrorMessage,
    paidListingExists,
    onDeactivateClick,
    onPublishClick,
}) => {
    const isScreenMobile = useSelector(state => makeIsScreenConstellationMobile(state));
    const isScreenTablet = useSelector(state => makeIsScreenConstellationTablet(state));
    const {
        impersonatorEmail,
        impersonatorUserToken,
        roles,
        features: { canUpdateListing },
    } = useSelector(state => UserSelectors.makeUser(state));
    const {
        leadGuarantee: {
            isEligible: isEligibleForLeadGuarantee,
        },
    } = useSelector(state => PropertiesSelectors.makeCurrentProperty(state));

    const isAdmin = isPostingPathAdmin(roles);
    const isUserImpersonating = Boolean(
        impersonatorEmail &&
        impersonatorUserToken &&
        isAdmin,
    );
    const buttonText = active ? 'Deactivate' : 'Publish';
    const { overview: { status, displayStatus } } = syndication;
    const isDuplicatePaidListingPosted = hasRestrictionErrorMessage && paidListingExists;
    const disablePublishIfDuplicatePaidListingPosted = !active && isInPaidMarket && isDuplicatePaidListingPosted;
    const renderPricingContent = !isActivationBlocked && !disablePublishIfDuplicatePaidListingPosted;
    const isFree = !isPaymentRequired;
    const isUserImpersonatingPaidMarketListing = isUserImpersonating && !active && !isFree && !isPaymentValid;
    const shouldDisablePublishListing = isUserImpersonatingPaidMarketListing
        || disablePublishIfDuplicatePaidListingPosted
        || (!active && isActivationBlocked);

    const handleOnButtonClick = () => (active ? onDeactivateClick(isFree) : onPublishClick(isFree));
    const renderGuaranteeIcon = () => (!active && isEligibleForLeadGuarantee)
        && <StyledGuaranteeIcon src={zGuaranteeIcon} />;

    if (isEditable && canUpdateListing && (!isPaidMultifamily || isAdmin)) {
        return (
            <StickyListingFooterContainer>
                <Container>
                    <Flex
                        display="flex"
                        justifyContent="space-between"
                    >
                        <StyledFlexContainer>
                            {renderPricingContent ? (
                                <>
                                    <StyledTagSpacer marginRight="xs">
                                        <Tag
                                            appearance={getTagTypeByKey(STATUS_KEY.LISTING_SUMMARY, status)}
                                        >
                                            {displayStatus}
                                        </Tag>
                                    </StyledTagSpacer>
                                    {renderGuaranteeIcon()}
                                    <StyledMiddleFlex>
                                        <LearnMore isInPaidMarket={isInPaidMarket} syndication={syndication} />
                                        <AdditionalInformation
                                            active={active}
                                            isFree={isFree}
                                            isInPaidMarket={isInPaidMarket}
                                            isMobile={isScreenMobile}
                                            isPaidMultifamily={isPaidMultifamily}
                                            isScreenTablet={isScreenTablet}
                                            price={price}
                                            syndication={syndication}
                                        />
                                    </StyledMiddleFlex>
                                </>
                            ) : (
                                <ErrorMessage
                                    isActive={active}
                                    // eslint-disable-next-line max-len
                                    disablePublishIfDuplicatePaidListingPosted={disablePublishIfDuplicatePaidListingPosted}
                                />
                            )}
                        </StyledFlexContainer>
                        <Button
                            buttonType="primary"
                            disabled={shouldDisablePublishListing}
                            onClick={handleOnButtonClick}
                        >
                            {isScreenMobile || isScreenTablet ? buttonText : `${buttonText} Listing`}
                        </Button>
                    </Flex>
                </Container>
            </StickyListingFooterContainer>
        );
    }
    return null;
};

ConnectedListingFooter.propTypes = propTypes;
ConnectedListingFooter.defaultProps = defaultProps;

export default ConnectedListingFooter;
